import {
  NODE_LABELS,
  ORG_NODE_LABELS,
  PLANT_PAINT_GHOST_PARENT_ENDPOINT,
  RELATIONSHIP_TYPES,
  validateProvenanceForWrite,
  type Provenance,
  type NodeLabel
} from '@atbash-labs/ontology-core'
import {
  STRICT_CYPHER_WRITE_POLICY,
  buildSchemaSnapshot,
  validateCypherWrite,
  validateNodeIdForLabel,
  type SchemaSnapshot
} from '@atbash-labs/ontology-cypher-validator'
import { batchMergeNodes, batchMergeRelationships } from './batch'
import {
  nodeDeletedDelta,
  nodeOverwrittenDelta,
  relationshipDeletedDelta,
  relationshipOverwrittenDelta
} from './capture-deltas'
import {
  formatUnknownPropertyKeyMessage,
  nodePropertiesRejectReason,
  relationshipPropertiesRejectReason,
  unknownNodePropertyKeys,
  unknownRelationshipPropertyKeys,
  type UnknownPropertyKey
} from './property-schema-check'
import { safeKey, safeLabel, safeRelType } from './utils/cypher-safety'
import { relId } from './utils/rel-id'
import type {
  BatchRelationshipInput,
  BatchResult,
  CreateNodeInput,
  GraphClient,
  WriteDelta
} from './types'

/**
 * Shared validated graph-write primitives (not ingest-only): used by the ingest
 * gateway, serdes, diff processor, and reconciler.
 *
 * Three intents live here:
 * 1. **Upsert** (`executeGraphUpsert`) — create-or-update nodes/relationships
 *    via `MERGE`. Unlike a key-only upsert, this path is schema-aware: labels,
 *    canonical URNs, required `id`/`name`, and relationship endpoint legality
 *    are validated before write. When `conflictGuard` is on, value-bearing
 *    property mismatches keep the existing graph value and return conflicts
 *    (callers may persist them); they do **not** last-write-wins. Nulls in
 *    upsert properties stay out of scope — the additive arm never removes.
 * 2. **Overwrite** (`overwriteNodePropertiesById` /
 *    `overwriteRelationshipPropertiesByEndpoints`) — deliberate property set
 *    on an existing node/relationship that bypasses the conflict guard
 *    (`MATCH` + `SET +=`, never `MERGE`). Null values in `set` remove keys
 *    (Neo4j `SET +=` semantics); the unset sentinel is `null`, never
 *    `undefined`. Node identity keys `id` / `name` cannot be unset.
 * 3. **Delete** — label-scoped node delete and endpoint-keyed relationship
 *    delete. Upsert never deletes.
 *
 * The producer supplies the URN (`properties.id`); this validates rather than
 * mints. The proxy `/cypher/write` does not validate — structured callers should
 * route through here.
 */
const ONTOLOGY_SNAPSHOT: SchemaSnapshot = buildSchemaSnapshot(NODE_LABELS, RELATIONSHIP_TYPES)

/**
 * Labels the ingest gateway / reconciler may create even though they sit in
 * `CYPHER_WRITE_BLOCKED_LABELS`. Derived from {@link ORG_NODE_LABELS} (the plant
 * spine) — blocked for free-form cypher-write but creatable through the
 * validated ingest rail when the corpus has no existing anchor. Single source
 * for gateway `allowLabels` and prompt never-create filtering.
 */
export const INGEST_CREATABLE_SPINE_LABELS: ReadonlySet<NodeLabel> = new Set<NodeLabel>(
  ORG_NODE_LABELS
)

/**
 * Additive upsert payload — nodes/relationships to create-or-update.
 * Property overwrites and deletes are separate intents (not fields here) so a
 * normal upsert cannot silently clobber contested values or remove entities.
 */
export interface GraphUpsertInput {
  nodes?: CreateNodeInput[]
  relationships?: BatchRelationshipInput[]
}

export interface RejectedWrite {
  scope: 'node' | 'relationship'
  /** Index into the corresponding input array. */
  index: number
  reason: string
}

/**
 * Non-blocking schema notice. Unknown property keys still write through —
 * this only informs callers/agents so they can prefer declared keys.
 */
export interface WriteWarning {
  scope: 'node' | 'relationship'
  /** Index into the corresponding input array (or overwrite/delete arm). */
  index: number
  key: string
  message: string
  suggestion?: string
  /** Complete declared-key set for this label/type. */
  declaredKeys: string[]
}

function toWriteWarnings(
  scope: 'node' | 'relationship',
  typeOrLabel: string,
  index: number,
  unknowns: UnknownPropertyKey[]
): WriteWarning[] {
  return unknowns.map((unknown) => ({
    scope,
    index,
    key: unknown.key,
    message: formatUnknownPropertyKeyMessage(typeOrLabel, unknown),
    ...(unknown.suggestion ? { suggestion: unknown.suggestion } : {}),
    declaredKeys: unknown.declaredKeys
  }))
}

export class GraphUpsertValidationError extends Error {
  readonly rejects: RejectedWrite[]

  constructor(rejects: RejectedWrite[]) {
    super(
      `Graph upsert rejected ${rejects.length} nonconformant write(s): ${rejects
        .map((reject) => `${reject.scope}[${reject.index}]: ${reject.reason}`)
        .join('; ')}`
    )
    this.name = 'GraphUpsertValidationError'
    this.rejects = [...rejects]
  }
}

export interface ValidatedGraphUpsert {
  /** Accepted nodes, ready for `batchMergeNodes` (unchanged from the input). */
  nodes: CreateNodeInput[]
  /** Accepted relationships, ready for `batchMergeRelationships` (unchanged). */
  relationships: BatchRelationshipInput[]
  rejects: RejectedWrite[]
  /**
   * Undeclared property keys on accepted rows. Never blocks the write;
   * rejected rows are omitted (the reject is the signal).
   */
  warnings: WriteWarning[]
}

function isSafe(value: string, check: (v: string) => string): boolean {
  try {
    check(value)
    return true
  } catch {
    return false
  }
}

function nodeRejectReason(
  node: CreateNodeInput,
  enforceBlockedLabels: boolean,
  allowLabels?: ReadonlySet<string>
): string | null {
  const label = node.label
  if (typeof label !== 'string' || !isSafe(label, safeLabel)) return `Invalid label "${label}"`
  if (!ONTOLOGY_SNAPSHOT.labels.has(label)) return `Unknown label "${label}"`
  if (
    enforceBlockedLabels &&
    ONTOLOGY_SNAPSHOT.cypherWriteBlockedLabels.has(label) &&
    !allowLabels?.has(label)
  ) {
    return `Label "${label}" is not writable`
  }
  const id = node.properties?.id
  const name = node.properties?.name
  if (typeof id !== 'string' || id.length === 0) return 'Missing properties.id'
  if (typeof name !== 'string' || name.length === 0) return 'Missing properties.name'
  const idCheck = validateNodeIdForLabel(label, id)
  if (!idCheck.ok) return idCheck.message
  return nodePropertiesRejectReason(label, node.properties)
}

function endpointRejectReason(
  side: 'source' | 'target',
  label: string,
  key: string
): string | null {
  if (typeof label !== 'string' || !isSafe(label, safeLabel))
    return `Invalid ${side} label "${label}"`
  if (!ONTOLOGY_SNAPSHOT.labels.has(label)) return `Unknown ${side} label "${label}"`
  if (typeof key !== 'string' || key.length === 0) return `Missing ${side} key`
  const idCheck = validateNodeIdForLabel(label, key)
  if (!idCheck.ok) return `${side} ${idCheck.message}`
  return null
}

function relRejectReason(rel: BatchRelationshipInput): string | null {
  const { type, sourceLabel, sourceKey, targetLabel, targetKey } = rel
  if (typeof type !== 'string' || !isSafe(type, safeRelType))
    return `Invalid relationship type "${type}"`
  if (!ONTOLOGY_SNAPSHOT.relationshipTypes.has(type)) return `Unknown relationship type "${type}"`
  if (rel.mergeKey != null && rel.mergeKey !== '' && !isSafe(rel.mergeKey, safeKey)) {
    return `Invalid relationship mergeKey "${rel.mergeKey}" (endpoint node property name, default "id")`
  }
  if (rel.mergeProperties) {
    for (const [key, value] of Object.entries(rel.mergeProperties)) {
      if (!isSafe(key, safeKey)) return `Invalid relationship merge property "${key}"`
      if (typeof value !== 'string' && typeof value !== 'number') {
        return `Relationship merge property "${key}" must be a string or number`
      }
    }
  }
  return (
    endpointRejectReason('source', sourceLabel, sourceKey) ??
    endpointRejectReason('target', targetLabel, targetKey) ??
    relationshipPropertiesRejectReason(type, rel.properties)
  )
}

function provenanceRejectReason(provenance: unknown): string | null {
  if (provenance === null || provenance === undefined) return 'Missing provenance'
  if (typeof provenance !== 'object' || Array.isArray(provenance)) return 'Missing provenance'

  const candidate = provenance as Provenance
  const validation = validateProvenanceForWrite(candidate)
  if (!validation.valid) {
    return `Invalid provenance: ${validation.errors.join('; ')}`
  }
  if (typeof candidate.source_file !== 'string' || candidate.source_file.trim().length === 0) {
    return 'Invalid provenance: provenance.source_file is required'
  }
  return null
}

export interface ValidateGraphUpsertOptions {
  /**
   * Enforce the agent-only `CYPHER_WRITE_BLOCKED_LABELS` set — reject nodes
   * whose label may not be agent-created. That set exists to stop the free-form
   * cypher-write agent tool from fabricating labels owned by deterministic
   * ingest, so it is the set of labels those producers must be able to write.
   * Deterministic producers (serdes, diff processor) pass `false`; the agent
   * sandbox gateway leaves it `true`. Defaults to `true` (secure-by-default): a
   * new agent-write path enforces the guardrail unless it explicitly opts out.
   * Every other conformance check (safe + known label, canonical URN form,
   * relationship endpoint legality) runs regardless of this flag.
   */
  enforceBlockedLabels?: boolean
  /**
   * When `enforceBlockedLabels` is true, labels in this set pass the blocked
   * check (every other conformance check still runs). Ignored when
   * `enforceBlockedLabels` is false. Ingest passes
   * {@link INGEST_CREATABLE_SPINE_LABELS}; free-form cypher-write omits it.
   */
  allowLabels?: ReadonlySet<string>
  /**
   * When true, nodes/relationships without complete ingest provenance are
   * rejected. Enabled by the ingest gateway only; serdes paths build provenance
   * in code and leave this off. Overwrite intents are out of scope (target keeps
   * its provenance; attribution lives in the write log / deltas).
   */
  requireProvenance?: boolean
  /** Extra properties applied to every node at write time; validated per label. */
  nodeAdditionalProps?: Record<string, unknown>
  /**
   * Skip undeclared-key warnings. Experion stamps every instance param, so
   * warning construction would allocate tens of thousands of messages before
   * the first write.
   */
  skipUnknownPropertyWarnings?: boolean
}

/**
 * Schema-validate upsert intents before `batchMerge*`.
 *
 * This is the part a naive "upsert by id" skips: each node must have a known
 * safe label, canonical `properties.id` for that label, non-empty `name`,
 * declared property types/enums that match, and (when enforced) an
 * agent-writable label; each relationship must have a known type plus legal
 * labeled endpoints (`validateCypherWrite` on a representative MERGE) and
 * conforming edge properties. Rejects are per-index; accepted rows still pass
 * so one bad intent never blocks the batch.
 *
 * Does **not** apply conflict-guard property logic — that runs at execute time
 * in `batchMerge*` when {@link ExecuteGraphUpsertOptions.conflictGuard} is on.
 */
export function validateGraphUpsert(
  input: GraphUpsertInput,
  opts: ValidateGraphUpsertOptions = {}
): ValidatedGraphUpsert {
  const enforceBlockedLabels = opts.enforceBlockedLabels ?? true
  const allowLabels = opts.allowLabels
  const requireProvenance = opts.requireProvenance ?? false
  const nodeAdditionalProps = opts.nodeAdditionalProps
  const rejects: RejectedWrite[] = []
  const warnings: WriteWarning[] = []

  const nodes: CreateNodeInput[] = []
  ;(input.nodes ?? []).forEach((node, index) => {
    const nodeToValidate = nodeAdditionalProps
      ? { ...node, properties: { ...node.properties, ...nodeAdditionalProps } }
      : node
    const reason =
      nodeRejectReason(nodeToValidate, enforceBlockedLabels, allowLabels) ??
      (requireProvenance ? provenanceRejectReason(node.provenance) : null)
    if (reason !== null) {
      rejects.push({ scope: 'node', index, reason })
      return
    }
    if (!opts.skipUnknownPropertyWarnings) {
      warnings.push(
        ...toWriteWarnings(
          'node',
          node.label,
          index,
          unknownNodePropertyKeys(node.label, nodeToValidate.properties)
        )
      )
    }
    nodes.push(node)
  })

  const relGroups = new Map<string, { rels: BatchRelationshipInput[]; indices: number[] }>()
  ;(input.relationships ?? []).forEach((rel, index) => {
    const reason =
      relRejectReason(rel) ?? (requireProvenance ? provenanceRejectReason(rel.provenance) : null)
    if (reason !== null) {
      rejects.push({ scope: 'relationship', index, reason })
      return
    }
    const key = `${rel.type}|${rel.sourceLabel}|${rel.targetLabel}`
    let group = relGroups.get(key)
    if (!group) {
      group = { rels: [], indices: [] }
      relGroups.set(key, group)
    }
    group.rels.push(rel)
    group.indices.push(index)
  })

  const relationships: BatchRelationshipInput[] = []
  for (const group of relGroups.values()) {
    const sample = group.rels[0]
    const probe =
      `MATCH (a:${safeLabel(sample.sourceLabel)} {id: $from})\n` +
      `MATCH (b:${safeLabel(sample.targetLabel)} {id: $to})\n` +
      `MERGE (a)-[r:${safeRelType(sample.type)}]->(b)\n` +
      `SET r += $props`
    const check = validateCypherWrite(probe, ONTOLOGY_SNAPSHOT, {
      params: { from: sample.sourceKey, to: sample.targetKey },
      policy: STRICT_CYPHER_WRITE_POLICY
    })
    if (!check.ok) {
      const reason = check.errors.map((e) => e.message).join('; ')
      group.indices.forEach((index) => rejects.push({ scope: 'relationship', index, reason }))
      continue
    }
    if (!opts.skipUnknownPropertyWarnings) {
      for (let i = 0; i < group.rels.length; i++) {
        const rel = group.rels[i]
        const index = group.indices[i]
        warnings.push(
          ...toWriteWarnings(
            'relationship',
            rel.type,
            index,
            unknownRelationshipPropertyKeys(rel.type, rel.properties)
          )
        )
      }
    }
    relationships.push(...group.rels)
  }

  return { nodes, relationships, rejects, warnings }
}

export interface ExecuteGraphUpsertOptions {
  /**
   * Warn instead of drop. When true, nonconformant writes are logged (via `log`)
   * but the **original** input is still written — preserving a producer's
   * existing behavior while its conformance is being verified. When false
   * (default, strict), only the validated writes are applied. There is no global
   * default beyond strict on purpose: the new gateway must enforce, while
   * already-shipped producers opt into warn and flip to strict per-site once
   * their writes are proven clean.
   */
  warnOnly?: boolean
  /**
   * Forwarded to `validateGraphUpsert`. Deterministic producers pass `false` so
   * they can write the labels they own; the agent sandbox gateway leaves it
   * `true`. Defaults to `true`. See {@link ValidateGraphUpsertOptions}.
   */
  enforceBlockedLabels?: boolean
  /** Forwarded to `validateGraphUpsert`. See {@link ValidateGraphUpsertOptions}. */
  allowLabels?: ReadonlySet<string>
  /** Forwarded to `validateGraphUpsert`. See {@link ValidateGraphUpsertOptions}. */
  requireProvenance?: boolean
  /** Throw before issuing writes when validation rejects any input row. */
  throwOnRejects?: boolean
  /** Logger for the nonconformance summary; the lib owns the message format. */
  log?: (message: string) => void
  /** Passthrough to `batchMergeNodes` (e.g. `additionalProps`, `onBatchComplete`). */
  nodeOpts?: Parameters<typeof batchMergeNodes>[2]
  /** Passthrough to `batchMergeRelationships` (e.g. `batchSize`, `onBatchComplete`). */
  relOpts?: Parameters<typeof batchMergeRelationships>[2]
  /** Forwarded to `validateGraphUpsert`. See {@link ValidateGraphUpsertOptions}. */
  skipUnknownPropertyWarnings?: boolean
  /**
   * Merge evidence_items on re-upsert instead of replacing provenance.
   * Forwarded to both `batchMergeNodes` and `batchMergeRelationships`.
   * Typical for multi-document ingest; off for single-authority producers.
   */
  accumulateEvidence?: boolean
  /**
   * Property conflict policy for this upsert (default off).
   *
   * When **true** (ingest gateway): on a value-bearing property mismatch, keep
   * the existing graph value, omit the incoming key from the MERGE SET, and
   * return a conflict entry in {@link ExecuteGraphUpsertResult.conflicts}. The
   * caller (e.g. ingest gateway) may persist those rows for a reconciler —
   * this function does not write Postgres. Re-upserting the same contested
   * value is a no-op on that key; use {@link overwriteNodePropertiesById} to
   * change it deliberately.
   *
   * When **false** / omitted (serdes, diff, reconciler settle): MERGE applies
   * properties without keep-and-conflict behavior (closer to a conventional
   * upsert). Schema validation still always runs.
   */
  conflictGuard?: boolean
  /**
   * Diff value-bearing props vs pre-read maps and return {@link WriteDelta}
   * entries on batch merge results. Forwarded to `batchMergeNodes` /
   * `batchMergeRelationships`.
   */
  captureDeltas?: boolean
}

export interface ExecuteGraphUpsertResult {
  /** Native `batchMergeNodes` result (created/updated counts + `createdIds`/`updatedIds`/`errors`). */
  nodes: BatchResult
  /** Native `batchMergeRelationships` result. */
  relationships: {
    created: number
    merged: number
    conflicts: BatchResult['conflicts']
    deltas: WriteDelta[]
  }
  /**
   * The relationship intents actually applied (strict mode: the validated set;
   * `warnOnly`: the original input). `batchMergeRelationships` reports counts
   * only, so callers that need to record which edges were written (write log)
   * read them here.
   */
  appliedRelationships: BatchRelationshipInput[]
  /** Schema / conformance rejects from {@link validateGraphUpsert} (not property conflicts). */
  rejects: RejectedWrite[]
  /**
   * Undeclared property-key notices from {@link validateGraphUpsert}.
   * Non-blocking — never feeds into caller `ok` decisions.
   */
  warnings: WriteWarning[]
  /**
   * Property mismatches kept under {@link ExecuteGraphUpsertOptions.conflictGuard}.
   * Empty when the guard is off. Distinct from `rejects` (validation failures).
   */
  conflicts: BatchResult['conflicts']
}

const EMPTY_NODE_RESULT: BatchResult = {
  created: 0,
  updated: 0,
  unchanged: 0,
  errors: [],
  createdIds: [],
  updatedIds: [],
  conflicts: [],
  deltas: []
}

/**
 * Validate then upsert nodes/relationships.
 *
 * **Not a key-only upsert.** Always schema-validates intents, then `MERGE`s.
 * Property behavior depends on {@link ExecuteGraphUpsertOptions.conflictGuard}:
 * with the guard on, mismatched value-bearing props stay on the existing node
 * and appear in `conflicts`; with it off, properties apply without that keep
 * policy. To force a property change under a guarded caller, use
 * {@link overwriteNodePropertiesById} (or a caller that turns the guard off).
 *
 * Strict mode (default) applies only validated writes; `warnOnly` applies the
 * original input and logs rejects (serdes rollout valve). Returns native
 * `batchMerge*` results so each consumer can shape its response.
 */
export async function executeGraphUpsert(
  client: GraphClient,
  input: GraphUpsertInput,
  opts: ExecuteGraphUpsertOptions = {}
): Promise<ExecuteGraphUpsertResult> {
  const validated = validateGraphUpsert(input, {
    enforceBlockedLabels: opts.enforceBlockedLabels,
    allowLabels: opts.allowLabels,
    requireProvenance: opts.requireProvenance,
    nodeAdditionalProps: opts.nodeOpts?.additionalProps,
    skipUnknownPropertyWarnings: opts.skipUnknownPropertyWarnings
  })
  if (validated.rejects.length > 0) {
    opts.log?.(
      `graph write: ${validated.rejects.length} nonconformant write(s) ${
        opts.throwOnRejects ? 'aborted' : opts.warnOnly ? 'kept (warn-mode)' : 'rejected'
      } — ${validated.rejects.map((r) => r.reason).join('; ')}`
    )
    if (opts.throwOnRejects) throw new GraphUpsertValidationError(validated.rejects)
  }

  const nodes = opts.warnOnly ? (input.nodes ?? []) : validated.nodes
  const relationships = opts.warnOnly ? (input.relationships ?? []) : validated.relationships

  const writeOpts = {
    accumulateEvidence: opts.accumulateEvidence,
    conflictGuard: opts.conflictGuard,
    captureDeltas: opts.captureDeltas
  }

  const nodeResult = nodes.length
    ? await batchMergeNodes(client, nodes, { ...opts.nodeOpts, ...writeOpts })
    : EMPTY_NODE_RESULT
  const relResult = relationships.length
    ? await batchMergeRelationships(client, relationships, { ...opts.relOpts, ...writeOpts })
    : { created: 0, merged: 0, conflicts: [], deltas: [] }

  return {
    nodes: nodeResult,
    relationships: relResult,
    appliedRelationships: relationships,
    rejects: validated.rejects,
    warnings: validated.warnings,
    conflicts: [...nodeResult.conflicts, ...relResult.conflicts]
  }
}

function toNumber(value: unknown): number {
  if (value === null || value === undefined) return 0
  if (typeof value === 'object' && 'toNumber' in (value as object)) {
    return (value as { toNumber: () => number }).toNumber()
  }
  return Number(value)
}

/** Delete-node intent: `{ id, label }` — label-scoped, URN-validated. */
export interface DeleteNodeIntent {
  id: string
  label: NodeLabel
}

/** Delete-relationship intent — create shape minus properties. */
export type DeleteRelationshipIntent = Pick<
  BatchRelationshipInput,
  'type' | 'sourceLabel' | 'sourceKey' | 'targetLabel' | 'targetKey'
>

/**
 * Overwrite intent: set properties on an existing label-scoped node.
 * Distinct from upsert — this is the deliberate conflict-guard bypass.
 * Null values in `set` remove those keys; `id` and `name` cannot be unset.
 */
export interface OverwriteNodeIntent {
  id: string
  label: NodeLabel
  set: Record<string, unknown>
}

/**
 * Overwrite intent for an existing relationship — delete shape + `set`.
 * Null values in `set` remove those keys (same Neo4j `SET +=` semantics).
 */
export interface OverwriteRelationshipIntent {
  type: string
  sourceLabel: NodeLabel
  sourceKey: string
  targetLabel: NodeLabel
  targetKey: string
  set: Record<string, unknown>
}

/** Validate a delete-node intent; returns a reason or null when accepted. */
export function validateDeleteNodeIntent(raw: unknown): string | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return 'Delete node must be an object { id, label }'
  }
  const { id, label } = raw as { id?: unknown; label?: unknown }
  if (typeof label !== 'string' || !isSafe(label, safeLabel)) return `Invalid label "${label}"`
  if (!ONTOLOGY_SNAPSHOT.labels.has(label)) return `Unknown label "${label}"`
  if (typeof id !== 'string' || id.length === 0) return 'Missing id'
  const idCheck = validateNodeIdForLabel(label, id)
  if (!idCheck.ok) return idCheck.message
  return null
}

/**
 * Validate a delete-relationship intent through the upsert relationship rail
 * (identifier/URN checks plus endpoint legality via `validateCypherWrite`).
 */
export function validateDeleteRelationshipIntent(raw: unknown): string | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return 'Delete relationship must be an object { type, sourceLabel, sourceKey, targetLabel, targetKey }'
  }
  const { rejects } = validateGraphUpsert({
    relationships: [raw as BatchRelationshipInput]
  })
  return rejects[0]?.reason ?? null
}

function overwriteSetRejectReason(set: unknown): string | null {
  if (set === null || typeof set !== 'object' || Array.isArray(set)) {
    return 'Overwrite set must be a non-empty object'
  }
  if (Object.keys(set as Record<string, unknown>).length === 0) {
    return 'Overwrite set must be a non-empty object'
  }
  return null
}

/** Reject nulling node identity / required-property keys. */
function nodeOverwriteIdentityUnsetReason(set: Record<string, unknown>): string | null {
  for (const key of ['id', 'name'] as const) {
    if (key in set && set[key] === null) {
      return `Cannot unset identity property "${key}"`
    }
  }
  return null
}

/** Validate an overwrite intent; `set` must be a non-empty plain object. */
export function validateOverwriteNodeIntent(raw: unknown): string | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return 'Overwrite must be an object { id, label, set }'
  }
  const { id, label, set } = raw as { id?: unknown; label?: unknown; set?: unknown }
  if (typeof label !== 'string' || !isSafe(label, safeLabel)) return `Invalid label "${label}"`
  if (!ONTOLOGY_SNAPSHOT.labels.has(label)) return `Unknown label "${label}"`
  if (typeof id !== 'string' || id.length === 0) return 'Missing id'
  const idCheck = validateNodeIdForLabel(label, id)
  if (!idCheck.ok) return idCheck.message
  const setReason = overwriteSetRejectReason(set)
  if (setReason !== null) return setReason
  const setObj = set as Record<string, unknown>
  const identityReason = nodeOverwriteIdentityUnsetReason(setObj)
  if (identityReason !== null) return identityReason
  return nodePropertiesRejectReason(label, setObj)
}

/**
 * Non-blocking undeclared-key notices for a node overwrite `set`.
 * Null values (unsets) are skipped — cleanup is not property-space drift.
 * Call only after {@link validateOverwriteNodeIntent} accepts the intent.
 */
export function overwriteNodePropertyWarnings(
  intent: OverwriteNodeIntent,
  index: number
): WriteWarning[] {
  return toWriteWarnings(
    'node',
    intent.label,
    index,
    unknownNodePropertyKeys(intent.label, intent.set, { ignoreNullValues: true })
  )
}

/**
 * Validate a relationship overwrite intent: endpoint rail from
 * {@link validateDeleteRelationshipIntent}, plus non-empty `set` and
 * relationship property-schema checks.
 */
export function validateOverwriteRelationshipIntent(raw: unknown): string | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return 'Overwrite relationship must be an object { type, sourceLabel, sourceKey, targetLabel, targetKey, set }'
  }
  const obj = raw as {
    type?: unknown
    sourceLabel?: unknown
    sourceKey?: unknown
    targetLabel?: unknown
    targetKey?: unknown
    set?: unknown
  }
  const endpointReason = validateDeleteRelationshipIntent({
    type: obj.type,
    sourceLabel: obj.sourceLabel,
    sourceKey: obj.sourceKey,
    targetLabel: obj.targetLabel,
    targetKey: obj.targetKey
  })
  if (endpointReason !== null) return endpointReason
  const setReason = overwriteSetRejectReason(obj.set)
  if (setReason !== null) return setReason
  return relationshipPropertiesRejectReason(String(obj.type), obj.set as Record<string, unknown>)
}

/**
 * Non-blocking undeclared-key notices for a relationship overwrite `set`.
 * Null values (unsets) are skipped. Call only after
 * {@link validateOverwriteRelationshipIntent} accepts the intent.
 */
export function overwriteRelationshipPropertyWarnings(
  intent: OverwriteRelationshipIntent,
  index: number
): WriteWarning[] {
  return toWriteWarnings(
    'relationship',
    intent.type,
    index,
    unknownRelationshipPropertyKeys(intent.type, intent.set, { ignoreNullValues: true })
  )
}

/** Deleted-node snapshot (id/label/name) for callers that log ghosts after delete. */
export interface DeletedNodeRef {
  id: string
  label: NodeLabel
  name: string
}

/** Containment edge captured before DETACH DELETE — used to place Plant View ghosts. */
export interface DetachedRelationshipRef {
  type: string
  sourceId: string
  targetId: string
}

/**
 * Relationship types whose endpoints place session-paint ghosts on a container.
 * Derived from ontology-core's Plant paint ghost parent map so delete-time
 * capture and UI placement cannot drift.
 */
export const GHOST_PLACEMENT_REL_TYPES: readonly string[] = Object.keys(
  PLANT_PAINT_GHOST_PARENT_ENDPOINT
)

export type DeleteNodeResult =
  | {
      status: 'deleted'
      node: DeletedNodeRef
      /** Containment edges removed by DETACH DELETE (for write-log / ghost placement). */
      detachedRelationships: DetachedRelationshipRef[]
      delta?: WriteDelta
    }
  | { status: 'not_found' }

export interface DeleteNodeOptions {
  captureDeltas?: boolean
}

function parseDetachedRelationships(raw: unknown): DetachedRelationshipRef[] {
  if (!Array.isArray(raw)) return []
  const out: DetachedRelationshipRef[] = []
  for (const edge of raw) {
    if (edge === null || typeof edge !== 'object' || Array.isArray(edge)) continue
    const type = (edge as { type?: unknown }).type
    const sourceId = (edge as { sourceId?: unknown }).sourceId
    const targetId = (edge as { targetId?: unknown }).targetId
    if (typeof type !== 'string' || type.length === 0) continue
    if (typeof sourceId !== 'string' || sourceId.length === 0) continue
    if (typeof targetId !== 'string' || targetId.length === 0) continue
    out.push({ type, sourceId, targetId })
  }
  return out
}

/**
 * DETACH DELETE a node by label-scoped id. Captures `name` and containment
 * edges before delete so callers can record ghosts after the node is gone.
 */
export async function deleteNodeByLabelId(
  client: GraphClient,
  label: NodeLabel,
  id: string,
  opts: DeleteNodeOptions = {}
): Promise<DeleteNodeResult> {
  const captureDeltas = opts.captureDeltas === true
  const { rows } = await client.write(
    captureDeltas
      ? `MATCH (n:${safeLabel(label)} {id: $id})
         WITH n, coalesce(toString(n.name), '') AS name, properties(n) AS props
         OPTIONAL MATCH (n)-[r]-()
         WHERE type(r) IN $containmentTypes
         WITH n, name, props, [edge IN collect(
           CASE WHEN r IS NULL THEN null
           ELSE { type: type(r), sourceId: startNode(r).id, targetId: endNode(r).id }
           END
         ) WHERE edge IS NOT NULL] AS edges
         DETACH DELETE n
         RETURN $id AS id, name, props, edges`
      : `MATCH (n:${safeLabel(label)} {id: $id})
         WITH n, coalesce(toString(n.name), '') AS name
         OPTIONAL MATCH (n)-[r]-()
         WHERE type(r) IN $containmentTypes
         WITH n, name, [edge IN collect(
           CASE WHEN r IS NULL THEN null
           ELSE { type: type(r), sourceId: startNode(r).id, targetId: endNode(r).id }
           END
         ) WHERE edge IS NOT NULL] AS edges
         DETACH DELETE n
         RETURN $id AS id, name, edges`,
    { id, containmentTypes: [...GHOST_PLACEMENT_REL_TYPES] }
  )
  if (rows.length === 0) return { status: 'not_found' }
  const delta = captureDeltas
    ? nodeDeletedDelta(id, label, (rows[0]?.props as Record<string, unknown>) ?? {})
    : undefined
  return {
    status: 'deleted',
    node: { id, label, name: String(rows[0]?.name ?? '') },
    detachedRelationships: parseDetachedRelationships(rows[0]?.edges),
    ...(delta ? { delta } : {})
  }
}

export interface DeleteRelationshipOptions {
  captureDeltas?: boolean
}

export type DeleteRelationshipResult =
  | { status: 'deleted'; delta?: WriteDelta }
  | { status: 'not_found' }

/**
 * Delete the relationship matching the create-shape endpoints
 * (`type` + labeled source/target keys). Same MATCH shape as
 * `batchMergeRelationships`.
 */
export async function deleteRelationshipByEndpoints(
  client: GraphClient,
  rel: Pick<
    BatchRelationshipInput,
    'type' | 'sourceLabel' | 'sourceKey' | 'targetLabel' | 'targetKey'
  >,
  opts: DeleteRelationshipOptions = {}
): Promise<DeleteRelationshipResult> {
  const captureDeltas = opts.captureDeltas === true
  const { rows } = await client.write(
    captureDeltas
      ? `MATCH (a:${safeLabel(rel.sourceLabel)} {id: $sourceKey})
              -[r:${safeRelType(rel.type)}]->
              (b:${safeLabel(rel.targetLabel)} {id: $targetKey})
         WITH r, properties(r) AS props
         DELETE r
         RETURN count(*) AS deleted, props`
      : `MATCH (a:${safeLabel(rel.sourceLabel)} {id: $sourceKey})
              -[r:${safeRelType(rel.type)}]->
              (b:${safeLabel(rel.targetLabel)} {id: $targetKey})
         DELETE r
         RETURN count(*) AS deleted`,
    { sourceKey: rel.sourceKey, targetKey: rel.targetKey }
  )
  if (toNumber(rows[0]?.deleted) === 0) return { status: 'not_found' }
  const delta = captureDeltas
    ? relationshipDeletedDelta(
        rel.type,
        rel.sourceKey,
        rel.targetKey,
        (rows[0]?.props as Record<string, unknown>) ?? {}
      )
    : undefined
  return { status: 'deleted', ...(delta ? { delta } : {}) }
}

export interface OverwriteNodeOptions {
  captureDeltas?: boolean
}

export type OverwriteNodeResult =
  | { status: 'applied'; appliedKeys: string[]; delta?: WriteDelta }
  | { status: 'not_found' }

export interface OverwriteRelationshipOptions {
  captureDeltas?: boolean
}

export type OverwriteRelationshipResult =
  | { status: 'applied'; appliedKeys: string[]; delta?: WriteDelta }
  | { status: 'not_found' }

/**
 * Deliberate property set on an existing label-scoped node (`SET n += $props`).
 *
 * Unlike {@link executeGraphUpsert} with `conflictGuard: true`, this always
 * writes the given keys — it does not keep-existing or return conflicts.
 * Null-valued keys remove properties (Neo4j `SET +=` semantics); captured
 * deltas record removed keys as `{ before, after: null }`. Callers that
 * persist conflict rows (ingest gateway) should resolve matching open
 * conflicts for `appliedKeys` after a successful apply. Prefer upsert for
 * first writes and agreeing re-writes; use this only when changing an
 * already-set or contested value is intentional.
 */
export async function overwriteNodePropertiesById(
  client: GraphClient,
  label: NodeLabel,
  id: string,
  props: Record<string, unknown>,
  opts: OverwriteNodeOptions = {}
): Promise<OverwriteNodeResult> {
  const captureDeltas = opts.captureDeltas === true
  const appliedKeys = Object.keys(props)
  const { rows } = await client.write(
    captureDeltas
      ? `MATCH (n:${safeLabel(label)} {id: $id})
         WITH n, properties(n) AS before
         SET n += $props
         RETURN n.id AS id, before`
      : `MATCH (n:${safeLabel(label)} {id: $id})
         SET n += $props
         RETURN n.id AS id`,
    { id, props }
  )
  if (rows.length === 0) return { status: 'not_found' }
  let delta: WriteDelta | undefined
  if (captureDeltas) {
    const before = (rows[0]?.before as Record<string, unknown>) ?? {}
    delta = nodeOverwrittenDelta(id, label, before, { ...before, ...props })
  }
  return { status: 'applied', appliedKeys, ...(delta ? { delta } : {}) }
}

/**
 * Deliberate property set on an existing relationship (`MATCH` + `SET r += $props`).
 *
 * Same labeled-endpoint MATCH as {@link deleteRelationshipByEndpoints} — never
 * `MERGE`s a missing edge (`not_found` instead). Null-valued keys remove
 * properties; captured deltas use `{ before, after: null }` for removals.
 */
export async function overwriteRelationshipPropertiesByEndpoints(
  client: GraphClient,
  rel: OverwriteRelationshipIntent,
  opts: OverwriteRelationshipOptions = {}
): Promise<OverwriteRelationshipResult> {
  const captureDeltas = opts.captureDeltas === true
  const appliedKeys = Object.keys(rel.set)
  const { rows } = await client.write(
    captureDeltas
      ? `MATCH (a:${safeLabel(rel.sourceLabel)} {id: $sourceKey})
              -[r:${safeRelType(rel.type)}]->
              (b:${safeLabel(rel.targetLabel)} {id: $targetKey})
         WITH r, properties(r) AS before
         SET r += $props
         RETURN count(*) AS matched, before`
      : `MATCH (a:${safeLabel(rel.sourceLabel)} {id: $sourceKey})
              -[r:${safeRelType(rel.type)}]->
              (b:${safeLabel(rel.targetLabel)} {id: $targetKey})
         SET r += $props
         RETURN count(*) AS matched`,
    { sourceKey: rel.sourceKey, targetKey: rel.targetKey, props: rel.set }
  )
  if (toNumber(rows[0]?.matched) === 0) return { status: 'not_found' }
  let delta: WriteDelta | undefined
  if (captureDeltas) {
    const before = (rows[0]?.before as Record<string, unknown>) ?? {}
    delta = relationshipOverwrittenDelta(
      relId(rel.sourceKey, rel.type, rel.targetKey),
      rel.type,
      rel.sourceKey,
      rel.targetKey,
      before,
      { ...before, ...rel.set }
    )
  }
  return { status: 'applied', appliedKeys, ...(delta ? { delta } : {}) }
}
